import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';

import {
  selectCheckBlockers,
  selectCheckSummary,
  selectInviteCounts,
  selectInviteesWithoutAccess,
  selectInviteTotal,
  selectUnjoinedMemberNames,
  useImportWizard,
} from '../../../../../features/members-import/state';
import { IMPORT_ACTIONS, IMPORT_STEPS } from '../../../constants';
import { ACCESS_FILTER, REVIEW_STATUS_FILTER, ROW_STATUS } from '../../../constants/reviewFilters.js';
import { PERMISSIONS } from '../../../constants/permissions.js';
import { ADMIN_CHOICE } from '../../../constants/inviteModes.js';
import { usePermission } from '../../../hooks/usePermission.js';
import { formatBatchLabel } from '../../../utils/formatBatchLabel.js';
import BeforeYouAddChecklist from './BeforeYouAddChecklist.jsx';
import ImportSummaryCard from './ImportSummaryCard.jsx';
import InvitationOptions from './InvitationOptions.jsx';

/**
 * Step 4: last check before anything is created. Reads and dispatches to the shared wizard
 * state; the primary button (add) lives in the wizard footer and is gated by the same selectors.
 */
function ImportCheckStep() {
  const { t, i18n } = useTranslation('people-access');
  const { state, dispatch, setStep } = useImportWizard();

  const summary = useMemo(() => selectCheckSummary(state), [state]);
  const blockers = useMemo(() => selectCheckBlockers(state), [state]);
  const counts = useMemo(() => selectInviteCounts(state), [state]);
  const noAccessPeople = useMemo(() => selectInviteesWithoutAccess(state), [state]);
  const inviteTotal = useMemo(() => selectInviteTotal(state), [state]);
  const unjoinedNames = useMemo(() => selectUnjoinedMemberNames(state), [state]);
  const people = useMemo(() => state.rows.filter((row) => row.status === ROW_STATUS.NEW), [state.rows]);
  const context = useMemo(() => ({ rows: state.rows, catalog: state.catalog }), [state.rows, state.catalog]);
  const batchLabel = formatBatchLabel(t, i18n.language, new Date(), state.file.name);

  const act = useCallback((type, payload) => dispatch({ type, payload }), [dispatch]);
  const backToReview = useCallback(
    (filters) => {
      act(IMPORT_ACTIONS.SET_REVIEW_FILTERS, filters);
      setStep(IMPORT_STEPS.REVIEW);
    },
    [act, setStep],
  );
  const handleDuplicate = useCallback((rowNumber, resolution) => act(IMPORT_ACTIONS.RESOLVE_DUPLICATE, { rowNumber, resolution }), [act]);
  const { allowed: canMakeAdmin } = usePermission(PERMISSIONS.MEMBERS_CHANGE_USER_TYPE);
  const handleAdmin = useCallback(
    (rowNumber, choice) => {
      // The button is disabled without the permission; this keeps a stale click from confirming (the backend must refuse too).
      if (choice === ADMIN_CHOICE.CONFIRM && !canMakeAdmin) return;
      act(IMPORT_ACTIONS.RESOLVE_ADMIN, { rowNumber, choice });
    },
    [act, canMakeAdmin],
  );
  const handleDomain = useCallback((rowNumber, choice) => act(IMPORT_ACTIONS.RESOLVE_OUTSIDE_DOMAIN, { rowNumber, choice }), [act]);
  const handleInvite = useCallback((change) => act(IMPORT_ACTIONS.SET_INVITE, change), [act]);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <ImportSummaryCard
        summary={summary}
        batchLabel={batchLabel}
        onAssignAccess={() => backToReview({ access: [ACCESS_FILTER.NONE], status: REVIEW_STATUS_FILTER.NEW })}
        onViewUpdates={() => backToReview({ access: [], status: REVIEW_STATUS_FILTER.UPDATE })}
        onViewRows={() => backToReview({ access: [], status: REVIEW_STATUS_FILTER.ERROR })}
      />
      <BeforeYouAddChecklist
        blockers={blockers}
        context={context}
        onDuplicate={handleDuplicate}
        onAdmin={handleAdmin}
        onOutsideDomain={handleDomain}
      />
      <InvitationOptions
        invite={state.invite}
        counts={counts}
        unjoinedNames={unjoinedNames}
        inviteTotal={inviteTotal}
        noAccessPeople={noAccessPeople}
        people={people}
        onChange={handleInvite}
      />
    </div>
  );
}

export default memo(ImportCheckStep);
